import { useEffect } from 'react';

interface PerformanceOptimizerProps {
  enableImageOptimization?: boolean;
  enableResourceHints?: boolean;
  criticalResourceUrls?: string[];
}

const PerformanceOptimizer = ({ 
  enableImageOptimization = true,
  enableResourceHints = true,
  criticalResourceUrls = []
}: PerformanceOptimizerProps) => {
  
  useEffect(() => {
    // Preload critical resources
    if (enableResourceHints && criticalResourceUrls.length > 0) {
      criticalResourceUrls.forEach(url => {
        const link = document.createElement('link');
        link.rel = 'preload';
        link.href = url;
        
        // Determine resource type based on URL
        if (url.match(/\.(jpg|jpeg|png|webp|avif)$/i)) {
          link.as = 'image';
        } else if (url.match(/\.(woff|woff2|ttf|otf)$/i)) {
          link.as = 'font';
          link.crossOrigin = 'anonymous';
        } else if (url.match(/\.css$/i)) {
          link.as = 'style';
        } else if (url.match(/\.js$/i)) {
          link.as = 'script';
        }
        
        document.head.appendChild(link);
      });
    }

    // Optimize images using Intersection Observer
    if (enableImageOptimization && 'IntersectionObserver' in window) {
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      }, {
        rootMargin: '50px 0px',
        threshold: 0.01
      });

      // Observe all images with data-src attribute
      const lazyImages = document.querySelectorAll('img[data-src]');
      lazyImages.forEach(img => imageObserver.observe(img));

      return () => imageObserver.disconnect();
    }
  }, [enableImageOptimization, enableResourceHints, criticalResourceUrls]);

  useEffect(() => {
    // Add viewport meta tag if not present
    if (!document.querySelector('meta[name="viewport"]')) {
      const viewportMeta = document.createElement('meta');
      viewportMeta.name = 'viewport';
      viewportMeta.content = 'width=device-width, initial-scale=1.0';
      document.head.appendChild(viewportMeta);
    }

    // Add performance marks for monitoring
    if ('performance' in window && 'mark' in performance) {
      performance.mark('performance-optimizer-loaded');
    }
  }, []);

  return null; // This is a utility component with no visual output
};

export default PerformanceOptimizer;